import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Search, Trash2 } from "lucide-react";
import EditableCell from "./EditableCell";
import {
  useGetAdminUsersQuery,
  useUpdateAdminUserMutation,
  useDeleteAdminUserMutation,
} from "@/redux/api/adminUsers.rtkq";
import { toast } from "sonner";

const UsersManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");

  // RTK Query hooks
  const {
    data: users = [],
    isLoading,
    error,
    refetch: refetchUsers,
  } = useGetAdminUsersQuery();
  const [updateUser] = useUpdateAdminUserMutation();
  const [deleteUser] = useDeleteAdminUserMutation();
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.displayName?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  // Helper function to safely format dates
  const formatDate = (date: string | undefined, fallback: string = "—"): string => {
    if (!date) return fallback;

    try {
      const dateObj = new Date(date);

      if (isNaN(dateObj.getTime())) {
        return "תאריך לא תקין";
      }

      return dateObj.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "תאריך לא תקין";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">{"טוען משתמשים..."}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{"שגיאה בטעינת המשתמשים"}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{"ניהול משתמשים"}</h3>
          <p className="text-sm text-slate-600">{"צפייה ועריכה של משתמשים במערכת"}</p>
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{"משתמשים במערכת"}</CardTitle>
              <CardDescription>{"רשימת כל המשתמשים הרשומים במערכת"}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={"חיפוש לפי שם או אימייל..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pr-10 w-64"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchUsers()}
                disabled={isLoading}
                className="border-slate-200 text-slate-800 hover:bg-slate-100"
              >
                <RefreshCw className="h-4 w-4 ml-2" />
                {isLoading ? "מרענן..." : "רענן"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-600">
              {searchQuery.trim()
                ? "לא נמצאו משתמשים התואמים לחיפוש."
                : "אין משתמשים במערכת."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">{"שם"}</TableHead>
                  <TableHead className="text-right">{"אימייל"}</TableHead>
                  <TableHead className="text-right">{"משרדים"}</TableHead>
                  <TableHead className="text-right">{"מנהל עליון"}</TableHead>
                  <TableHead className="text-right">{"סטטוס"}</TableHead>
                  <TableHead className="text-right">{"כניסה אחרונה"}</TableHead>
                  <TableHead className="text-right">{"תאריך יצירה"}</TableHead>
                  <TableHead className="text-right">{"פעולות"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="text-right">
                      <EditableCell
                        value={user.displayName || ""}
                        onSave={async (val) => { await updateUser({ id: user.id, updates: { displayName: val as string } }).unwrap(); }}
                        type="text"
                        displayValue={<span className="font-medium">{user.displayName || "—"}</span>}
                      />
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {user.email}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {user.offices.length > 0 ? (
                        <span className="text-slate-600">
                          {user.offices.map((o) => `${o.officeName} (${o.role === "office_admin" ? "מנהל" : "משתמש"})`).join(", ")}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={user.isSuperAdmin}
                        onSave={async (val) => { await updateUser({ id: user.id, updates: { isSuperAdmin: val as boolean } }).unwrap(); }}
                        type="toggle"
                        displayValue={
                          user.isSuperAdmin
                            ? <Badge variant="default" className="bg-purple-600">מנהל עליון</Badge>
                            : <span className="text-slate-400">—</span>
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={!user.isDisabled}
                        onSave={async (val) => { await updateUser({ id: user.id, updates: { isDisabled: !(val as boolean) } }).unwrap(); }}
                        type="toggle"
                        displayValue={
                          user.isDisabled
                            ? <Badge variant="destructive">מושבת</Badge>
                            : <Badge variant="default">פעיל</Badge>
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right text-sm text-slate-600">
                      {formatDate(user.lastLoginAt, "לא התחבר")}
                    </TableCell>
                    <TableCell className="text-right text-sm text-slate-600">
                      {formatDate(user.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeletingUserId(user.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!deletingUserId} onOpenChange={() => setDeletingUserId(null)}>
        <DialogContent className="max-w-sm" dir="rtl">
          <DialogHeader>
            <DialogTitle>מחיקת משתמש</DialogTitle>
            <DialogDescription>
              פעולה זו תמחק את המשתמש, כולל כל הרשאות המשרד וחשבון Firebase.
              לא ניתן לבטל פעולה זו.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeletingUserId(null)}>
              ביטול
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!deletingUserId) return;
                try {
                  await deleteUser(deletingUserId).unwrap();
                  toast.success("המשתמש נמחק בהצלחה");
                  setDeletingUserId(null);
                } catch (err) {
                  toast.error("שגיאה במחיקת המשתמש");
                }
              }}
            >
              מחק משתמש
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
